import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { API_URL } from '../Config';

const DemandeFormVehicule = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;
    
    const [formData, setFormData] = useState({
        vehicule_id: '',
        type_reparation: '',
        km_actuel: ''
    });
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [user, setUser] = useState(null);
    const [vehicules, setVehicules] = useState([]);
    const [vehiculesLoading, setVehiculesLoading] = useState(true);

    const token = localStorage.getItem('token');

    const typesReparation = [
        { value: 'entretien', label: 'Entretien régulier' },
        { value: 'reparation_mecanique', label: 'Réparation mécanique' },
        { value: 'reparation_carrosserie', label: 'Réparation carrosserie' },
        { value: 'vidange', label: 'Vidange' },
        { value: 'freinage', label: 'Freinage' },
        { value: 'suspension', label: 'Suspension' },
        { value: 'climatisation', label: 'Climatisation' },
        { value: 'pneumatique', label: 'Pneumatique' },
        { value: 'electricite', label: 'Électricité' },
        { value: 'diagnostic', label: 'Diagnostic' },
        { value: 'autre', label: 'Autre' }
    ];

    useEffect(() => {
        const userData = localStorage.getItem('user');
        if (userData) {
            const parsedUser = JSON.parse(userData);
            setUser(parsedUser);
            
            if (isEditMode) {
                if (!['validateur', 'chef_equipe'].includes(parsedUser.role)) {
                    navigate('/demandes-reparation');
                    return;
                }
            } else {
                if (!['client', 'mecanicien', 'validateur'].includes(parsedUser.role)) {
                    navigate('/demandes-reparation');
                    return;
                }
            }
        } else {
            navigate('/login');
            return;
        }
        
        fetchVehicules();
        
        if (isEditMode) {
            fetchDemande();
        }
    }, [id, navigate]);

    const fetchVehicules = async () => {
        try {
            setVehiculesLoading(true);
            const response = await axios.get(`${API_URL}/vehicules`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.data && Array.isArray(response.data)) {
                setVehicules(response.data);
            } else if (response.data && response.data.data) {
                setVehicules(response.data.data);
            } else {
                setVehicules([]);
            }
        } catch (err) {
            console.error('Erreur chargement véhicules:', err);
            setVehicules([]);
        } finally {
            setVehiculesLoading(false);
        }
    };

    const fetchDemande = async () => {
        try {
            const response = await axios.get(`${API_URL}/demandes-reparations/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setFormData({
                vehicule_id: response.data.vehicule_id,
                type_reparation: response.data.type_reparation || '',
                km_actuel: response.data.km_actuel || ''
            });
        } catch (err) {
            setError('Erreur lors du chargement de la demande');
            console.error('Erreur:', err);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'km_actuel' ? (value === '' ? '' : parseInt(value)) : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(false);

        if (!formData.vehicule_id) {
            setError('Veuillez sélectionner un véhicule');
            setLoading(false);
            return;
        }

        if (!formData.type_reparation.trim()) {
            setError('Veuillez sélectionner un type de réparation');
            setLoading(false);
            return;
        }

        if (!formData.km_actuel || formData.km_actuel < 0) {
            setError('Veuillez saisir un kilométrage valide');
            setLoading(false);
            return;
        }

        try {
            if (isEditMode) {
                await axios.put(`${API_URL}/demandes-reparations/${id}`, formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setSuccess('Demande mise à jour avec succès');
                setTimeout(() => navigate(`/demandes-reparation/${id}`), 1500);
            } else {
                const response = await axios.post(`${API_URL}/demandes-reparations`, formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setSuccess('Demande créée avec succès');
                setTimeout(() => navigate(`/demandes-reparation/${response.data.id}`), 1500);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Une erreur est survenue');
        } finally {
            setLoading(false);
        }
    };

    if (!user) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
                <div style={{ 
                    border: '3px solid #2563eb', 
                    borderTopColor: 'transparent',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    animation: 'spin 1s linear infinite'
                }}></div>
            </div>
        );
    }

    return (
        <div style={{ minHeight: '100vh', padding: '20px' }}>
            <div style={{ maxWidth: '900px', margin: '0 auto' }}>
                {/* Header */}
                <div style={{ marginBottom: '30px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <Link
                            to="/demandes-reparation"
                            style={{ 
                                display: 'inline-flex', 
                                alignItems: 'center', 
                                color: '#374151',
                                textDecoration: 'none',
                                fontSize: '14px',
                                fontWeight: '500'
                            }}
                        >
                            <ArrowLeftIcon style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                            Retour aux demandes
                        </Link>
                        
                    </div>
                    
                    <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#111827', marginBottom: '8px' }}>
                        {isEditMode ? 'Modifier la demande de réparation' : 'Nouvelle demande de réparation'}
                    </h1>
                    <p style={{ color: '#6b7280', fontSize: '15px' }}>
                        {isEditMode 
                            ? 'Modifiez les informations de la demande'
                            : 'Remplissez le formulaire pour créer une nouvelle demande'
                        }
                    </p>
                </div>

                {/* Formulaire */}
                <div style={{ backgroundColor: '#ffffff', border: '1px solid #d1d5db', padding: '0' }}>
                    {/* En-tête */}
                    <div style={{ 
                        padding: '20px 30px', 
                        borderBottom: '1px solid #e5e7eb',
                        backgroundColor: '#f9fafb'
                    }}>
                        <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: '0' }}>
                            Informations de la réparation
                        </h2>
                        <p style={{ fontSize: '14px', color: '#6b7280', margin: '4px 0 0 0' }}>
                            Tous les champs sont obligatoires
                        </p>
                    </div>

                    <div style={{ padding: '30px' }}>
                        {/* Messages */}
                        {error && (
                            <div style={{ 
                                marginBottom: '20px', 
                                padding: '12px 16px', 
                                backgroundColor: '#fef2f2',
                                border: '1px solid #fecaca',
                                display: 'flex',
                                alignItems: 'flex-start'
                            }}>
                                <ExclamationTriangleIcon style={{ width: '20px', height: '20px', color: '#dc2626', marginRight: '12px', flexShrink: 0 }} />
                                <span style={{ color: '#991b1b', fontSize: '14px' }}>{error}</span>
                            </div>
                        )}
                        
                        {success && (
                            <div style={{ 
                                marginBottom: '20px', 
                                padding: '12px 16px', 
                                backgroundColor: '#f0fdf4',
                                border: '1px solid #bbf7d0',
                                display: 'flex',
                                alignItems: 'flex-start'
                            }}>
                                <CheckCircleIcon style={{ width: '20px', height: '20px', color: '#16a34a', marginRight: '12px', flexShrink: 0 }} />
                                <span style={{ color: '#166534', fontSize: '14px' }}>{success}</span>
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            {/* Véhicule */}
                            <div style={{ marginBottom: '24px' }}>
                                <label style={{ 
                                    display: 'block', 
                                    fontSize: '14px', 
                                    fontWeight: '600', 
                                    color: '#111827',
                                    marginBottom: '8px'
                                }}>
                                    Véhicule <span style={{ color: '#dc2626' }}>*</span>
                                </label>
                                <select
                                    name="vehicule_id"
                                    value={formData.vehicule_id}
                                    onChange={handleChange}
                                    required
                                    disabled={vehiculesLoading || vehicules.length === 0}
                                    style={{
                                        width: '100%',
                                        padding: '10px 12px',
                                        border: '1px solid #d1d5db',
                                        fontSize: '14px',
                                        color: '#111827',
                                        backgroundColor: (vehiculesLoading || vehicules.length === 0) ? '#f3f4f6' : '#ffffff'
                                    }}
                                >
                                    <option value="">Sélectionnez un véhicule</option>
                                    {vehicules.map(vehicule => (
                                        <option key={vehicule.id} value={vehicule.id}>
                                            {vehicule.numero || 'N/A'} - {vehicule.marque} {vehicule.modele}
                                        </option>
                                    ))}
                                </select>
                                {vehiculesLoading ? (
                                    <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '6px' }}>Chargement des véhicules...</p>
                                ) : vehicules.length === 0 ? (
                                    <p style={{ fontSize: '13px', color: '#dc2626', marginTop: '6px' }}>Aucun véhicule disponible</p>
                                ) : (
                                    <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '6px' }}>Sélectionnez le véhicule concerné</p>
                                )}
                            </div>

                            {/* Type de réparation */}
                            <div style={{ marginBottom: '24px' }}>
                                <label style={{ 
                                    display: 'block', 
                                    fontSize: '14px', 
                                    fontWeight: '600', 
                                    color: '#111827',
                                    marginBottom: '8px'
                                }}>
                                    Type de réparation <span style={{ color: '#dc2626' }}>*</span>
                                </label>
                                <select
                                    name="type_reparation"
                                    value={formData.type_reparation}
                                    onChange={handleChange}
                                    required
                                    style={{
                                        width: '100%',
                                        padding: '10px 12px',
                                        border: '1px solid #d1d5db',
                                        fontSize: '14px',
                                        color: '#111827',
                                        backgroundColor: '#ffffff'
                                    }}
                                >
                                    <option value="">Choisissez un type</option>
                                    {typesReparation.map(type => (
                                        <option key={type.value} value={type.value}>
                                            {type.label}
                                        </option>
                                    ))}
                                </select>
                                <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '6px' }}>Type d'intervention nécessaire</p>
                            </div>

                            {/* Kilométrage */}
                            <div style={{ marginBottom: '30px' }}>
                                <label style={{ 
                                    display: 'block', 
                                    fontSize: '14px', 
                                    fontWeight: '600', 
                                    color: '#111827',
                                    marginBottom: '8px'
                                }}>
                                    Kilométrage actuel <span style={{ color: '#dc2626' }}>*</span>
                                </label>
                                <input
                                    type="number"
                                    name="km_actuel"
                                    value={formData.km_actuel}
                                    onChange={handleChange}
                                    required
                                    min="0"
                                    step="1"
                                    placeholder="Ex: 125000"
                                    style={{
                                        width: '100%',
                                        padding: '10px 12px',
                                        border: '1px solid #d1d5db',
                                        fontSize: '14px',
                                        color: '#111827',
                                        backgroundColor: '#ffffff'
                                    }}
                                />
                                <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '6px' }}>
                                    Kilométrage actuel du véhicule
                                </p>
                            </div>

                            {/* Boutons */}
                            <div style={{ 
                                borderTop: '1px solid #e5e7eb', 
                                paddingTop: '24px',
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: '12px'
                            }}>
                                <Link
                                    to="/demandes-reparation"
                                    style={{
                                        padding: '10px 20px',
                                        border: '1px solid #d1d5db',
                                        backgroundColor: '#ffffff',
                                        color: '#374151',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        textDecoration: 'none',
                                        display: 'inline-block'
                                    }}
                                >
                                    Annuler
                                </Link>
                                
                                <button
                                    type="submit"
                                    disabled={loading || vehiculesLoading || vehicules.length === 0}
                                    style={{
                                        padding: '10px 20px',
                                        border: 'none',
                                        backgroundColor: (loading || vehiculesLoading || vehicules.length === 0) ? '#d1d5db' : '#2563eb',
                                        color: '#ffffff',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        cursor: (loading || vehiculesLoading || vehicules.length === 0) ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    {loading ? 'Traitement...' : (isEditMode ? 'Mettre à jour' : 'Créer la demande')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                
            </div>
        </div>
    );
};

export default DemandeFormVehicule;